import React, { useEffect, useMemo, useState } from 'react';
import { Table, Card, Row, Col, Input, Tag, Typography, Space, Statistic, Progress, message } from 'antd';
import { TeamOutlined, SolutionOutlined, MedicineBoxOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { hospitalAdminService } from '../../services';

const { Text } = Typography;

// What each doctor in the hospital has actually done: how many patients are on
// their list, how many visits they have recorded, and what they prescribe most.
// One request for the whole hospital - the top ten drugs come down with the rows,
// so opening a doctor costs nothing.
const DoctorReports = () => {
    const { t } = useTranslation();
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchReport();
    }, []);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const res = await hospitalAdminService.getDoctorReports();
            setDoctors(res.data || []);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return doctors;

        return doctors.filter((doctor) => [doctor.name, doctor.specialization, doctor.branch?.name]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(term)));
    }, [doctors, search]);

    // Totalled off the filtered rows rather than fetched separately, so the figures
    // at the top can never disagree with the table under them.
    const totals = useMemo(() => filtered.reduce((sum, doctor) => ({
        patients: sum.patients + doctor.patients,
        visits: sum.visits + doctor.visits,
        prescribedItems: sum.prescribedItems + doctor.prescribedItems
    }), { patients: 0, visits: 0, prescribedItems: 0 }), [filtered]);

    const columns = [
        {
            title: t('doctor'),
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{record.name}</Text>
                    {record.specialization && (
                        <Text type="secondary" style={{ fontSize: 12 }}>{record.specialization}</Text>
                    )}
                </Space>
            )
        },
        {
            title: t('branch'),
            key: 'branch',
            render: (_, record) => record.branch?.name || '-'
        },
        {
            title: t('patients'),
            dataIndex: 'patients',
            key: 'patients',
            align: 'center',
            sorter: (a, b) => a.patients - b.patients,
            render: (value) => <Tag color="blue">{value}</Tag>
        },
        {
            title: t('visits'),
            dataIndex: 'visits',
            key: 'visits',
            align: 'center',
            // The hospital's busiest doctor is the first thing this page is opened for.
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.visits - b.visits,
            render: (value) => <Tag color="green">{value}</Tag>
        },
        {
            title: t('prescriptionsWritten'),
            dataIndex: 'prescribedItems',
            key: 'prescribedItems',
            align: 'center',
            sorter: (a, b) => a.prescribedItems - b.prescribedItems,
            render: (value, record) => (
                <Space size={4} wrap>
                    <Tag color="purple">{value}</Tag>
                    {record.distinctDrugs > 0 && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            {record.distinctDrugs} {t('drugs')}
                        </Text>
                    )}
                </Space>
            )
        },
        {
            title: t('mostPrescribed'),
            key: 'mostPrescribed',
            render: (_, record) => (record.topDrugs.length > 0 ? (
                <Tag icon={<MedicineBoxOutlined />} color="magenta">
                    {record.topDrugs[0].name} &times; {record.topDrugs[0].count}
                </Tag>
            ) : <Text type="secondary">-</Text>)
        }
    ];

    // The ten drugs this doctor writes most. Bars are scaled against their own top
    // drug, so the shape of one doctor's prescribing reads at a glance without
    // being flattened by a busier colleague.
    const topDrugsTable = (record) => {
        const busiest = record.topDrugs[0].count;

        return (
            <Table
                size="small"
                pagination={false}
                rowKey="name"
                dataSource={record.topDrugs}
                columns={[
                    {
                        title: '#',
                        key: 'rank',
                        width: 50,
                        render: (_, __, index) => index + 1
                    },
                    {
                        title: t('drugName'),
                        dataIndex: 'name',
                        key: 'name'
                    },
                    {
                        title: t('timesPrescribed'),
                        dataIndex: 'count',
                        key: 'count',
                        width: 240,
                        render: (count) => (
                            <Space>
                                <Progress
                                    percent={Math.round((count / busiest) * 100)}
                                    showInfo={false}
                                    size="small"
                                    style={{ width: 140 }}
                                />
                                <Text strong>{count}</Text>
                            </Space>
                        )
                    }
                ]}
            />
        );
    };

    return (
        <div className="page-container">
            <Row gutter={[16, 16]} align="middle" justify="space-between" className="page-header">
                <Col xs={24} md={12}>
                    <h1 className="page-title">{t('doctorReport')}</h1>
                </Col>
                <Col xs={24} md={8}>
                    <Input.Search
                        allowClear
                        placeholder={t('searchDoctors')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic title={t('doctors')} value={filtered.length} prefix={<UserOutlined />} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic title={t('patients')} value={totals.patients} prefix={<TeamOutlined />} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic title={t('visits')} value={totals.visits} prefix={<SolutionOutlined />} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('prescriptionsWritten')}
                            value={totals.prescribedItems}
                            prefix={<MedicineBoxOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            <Card className="table-card">
                <Table
                    columns={columns}
                    dataSource={filtered}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 900 }}
                    expandable={{
                        expandedRowRender: topDrugsTable,
                        // No expander on a doctor who has not prescribed anything yet -
                        // the "most prescribed" column already says so with a dash.
                        rowExpandable: (record) => record.topDrugs.length > 0,
                        columnTitle: t('topTenDrugs')
                    }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `${t('total')}: ${total}`
                    }}
                />
            </Card>
        </div>
    );
};

export default DoctorReports;
